import React, { useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';

import { ButtonGroup, Button, Card, CardHeader, CardBody, CardTitle, Table, Row, Col, UncontrolledTooltip } from 'reactstrap';

import { report } from '../../../api';
import UserContext from '../../UserContext';
import { RouteComponentProps } from 'react-router-dom';
import { ReportDB } from 'api/reports';
import { ReportTableRecord } from './ReportTableRecord';
import { ModalOverlay, ModalOverlayDelete } from 'components/Modal/Modal';
import { FaInfo, FaPlus, FaSearch } from 'react-icons/fa';
import { ResourceNotFoundException } from '../../../api/exceptions/ResourceNotFoundException';
import toast from 'react-hot-toast';
import { THSorter } from 'components/TableSorting/TableSorting';

interface Props extends RouteComponentProps {}

export const ReportTable = (props: Props) => {
	const { location, match, history } = props;
	const { jwt } = useContext(UserContext);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [loadingError, setLoadingError] = useState<boolean>(false);

	const [data, setData] = useState<Array<ReportDB>>([]);

	const [sortBy, setSortBy] = useState<keyof ReportDB>('title');
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

	const [copyRecordID, setCopyRecordID] = useState<null | string>(null);
	const [tableRecordDeleteID, setTableRecordDeleteID] = useState<null | string>(null);

	const getContents = async () => {
		setIsLoading(true);
		try {
			const reports = await report.list(jwt, { sort: sortBy, order: sortDir, page_size: 10000 });

			setData(reports);
		} catch (e) {
			console.log(e);
			setLoadingError(true);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getContents();
	}, []);

	const handleToolbarAddClick = () => {
		history.push({ pathname: `${location.pathname}/add` });
	};

	const handleRecordEdit = (docID: string) => {
		history.push({ pathname: `${location.pathname}/${docID}` });
	};

	const handleRecordCopy = (docID: string) => {
		setCopyRecordID(docID);
	};

	const handleRecordDelete = (docID: string) => {
		setTableRecordDeleteID(docID);
	};

	const handleTableRecordDeleteConfirm = async () => {
		try {
			await report.delete(jwt, tableRecordDeleteID!);

			const recordIndex = data.findIndex((record) => record.docID === tableRecordDeleteID);

			if (recordIndex > -1) {
				data.splice(recordIndex, 1);
			}

			setData(data);
			setTableRecordDeleteID(null);
		} catch (error) {
			if (error instanceof ResourceNotFoundException) {
				toast.error('Record already deleted');
				setTableRecordDeleteID(null);
			} else {
				toast.error('Unknown error occurred');

				console.log('error');
				console.log(error);
				Sentry.captureException(error);
			}
		}
	};

	const handleTableRecordDeleteConfirmCancel = () => {
		setTableRecordDeleteID(null);
	};

	const handleCopyConfirm = async () => {
		try {
			const response = await report.copy(jwt, copyRecordID!);

			setData((prevData) => [...prevData, response]);
			setCopyRecordID(null);
		} catch (error) {
			console.log('error');
			console.log(error);
			Sentry.captureException(error);
		}
	};

	const handleCopyCancel = () => {
		setCopyRecordID(null);
	};

	const handleSortHeader = (column: keyof ReportDB) => {
		if (sortBy === column) {
			setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
		} else {
			setSortBy(column);
			setSortDir('desc');
		}

		getContents();
	};

	return (
		<div className="report-table">
			<Row>
				<Col md="12">
					<Card>
						<CardHeader>
							<div className="pull-right">
								<Button color="info" className="d-flex align-items-center gap-1" onClick={handleToolbarAddClick}>
									<FaPlus /> Add Report
								</Button>
							</div>
							<CardTitle tag="h4">Reports</CardTitle>
						</CardHeader>
						<CardBody>
							<Table hover>
								<thead className="text-primary">
									<tr>
										<th style={{ width: '3%' }}></th>
										<THSorter
											sortBy={sortBy === 'title'}
											sortDir={sortDir}
											onClick={() => {
												handleSortHeader('title');
											}}
											style={{ width: '40%' }}
										>
											Title
										</THSorter>
										<THSorter
											sortBy={sortBy === 'type'}
											sortDir={sortDir}
											onClick={() => {
												handleSortHeader('type');
											}}
										>
											Type
										</THSorter>
										<THSorter
											sortBy={sortBy === 'outputFormat'}
											sortDir={sortDir}
											onClick={() => {
												handleSortHeader('outputFormat');
											}}
										>
											Output
										</THSorter>
										<THSorter
											sortBy={sortBy === 'last_run'}
											sortDir={sortDir}
											onClick={() => {
												handleSortHeader('last_run');
											}}
											type="numeric"
										>
											Last Run
										</THSorter>
										<th className="text-right" style={{ width: '20%' }}>
											Actions
										</th>
									</tr>
								</thead>
								<tbody>
									{loadingError ? (
										<tr>
											<td colSpan={6}>There was an error loading data.</td>
										</tr>
									) : isLoading ? (
										<tr>
											<td colSpan={6}>Loading...</td>
										</tr>
									) : data != null && data.length > 0 ? (
										data.map((record) => {
											return (
												<ReportTableRecord
													key={record.docID}
													data={record}
													onEdit={handleRecordEdit}
													onCopy={handleRecordCopy}
													onDelete={handleRecordDelete}
												/>
											);
										})
									) : (
										<tr>
											<td colSpan={5}>No reports found.</td>
										</tr>
									)}
								</tbody>
							</Table>
						</CardBody>
					</Card>
				</Col>
			</Row>
			<ModalOverlay
				show={copyRecordID != null}
				icon={FaInfo}
				title="Copy Report"
				primaryActionCallback={handleCopyConfirm}
				primaryActionText="Yes, Copy"
				secondaryActionCallback={handleCopyCancel}
			>
				Are you sure you want to copy this report?
			</ModalOverlay>
			<ModalOverlayDelete
				show={tableRecordDeleteID != null}
				title="Delete Report"
				primaryActionCallback={handleTableRecordDeleteConfirm}
				secondaryActionCallback={handleTableRecordDeleteConfirmCancel}
			>
				This action cannot be undone.
			</ModalOverlayDelete>
		</div>
	);
};
